import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileExport, faFileImport, faDownload, faUpload, faSpinner,
  faCheckCircle, faExclamationTriangle, faFileCsv, faCode,
  faExchangeAlt, faPlus, faTrash, faTimes, faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { dataTransferAPI } from '../services/api';
import '../styles/DataTransfer.css';
import useI18n from '../hooks/useI18n';

const DataTransfer: React.FC = () => {
  const { t } = useI18n();
  const [activeSection, setActiveSection] = useState<'export' | 'import'>('export');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Export state
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const [availableFields, setAvailableFields] = useState<string[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [fieldLabels, setFieldLabels] = useState<Record<string, string>>({});
  const [exporting, setExporting] = useState(false);

  // Import state
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importMode, setImportMode] = useState<'add' | 'overwrite'>('add');
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState<any[] | null>(null);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const showMsg = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  useEffect(() => {
    const fetchFields = async () => {
      try {
        const res = await dataTransferAPI.getExportFields();
        const rawFields = res.data.fields || res.data || [];
        const keys: string[] = [];
        const labels: Record<string, string> = {};
        rawFields.forEach((f: any) => {
          if (typeof f === 'string') {
            keys.push(f);
          } else {
            keys.push(f.key);
            if (f.label) labels[f.key] = f.label;
          }
        });
        setAvailableFields(keys);
        const defaultKeys = rawFields
          .filter((f: any) => typeof f === 'string' || f.default !== false)
          .map((f: any) => (typeof f === 'string' ? f : f.key));
        setSelectedFields(defaultKeys.length > 0 ? defaultKeys : keys);
        setFieldLabels(labels);
      } catch (err) {
        console.error('Failed to fetch export fields:', err);
      }
    };
    fetchFields();
  }, []);

  const toggleField = (field: string) => {
    setSelectedFields(prev =>
      prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
    );
  };

  const selectAllFields = () => setSelectedFields([...availableFields]);
  const deselectAllFields = () => setSelectedFields([]);

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      showMsg('error', t('dataTransfer.selectOneField'));
      return;
    }
    try {
      setExporting(true);
      const res = await dataTransferAPI.exportData({
        format: exportFormat,
        fields: selectedFields,
      });
      // Download the blob
      const blob = new Blob([res.data], {
        type: exportFormat === 'csv' ? 'text/csv' : 'application/json',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `items_export_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showMsg('success', t('dataTransfer.exportSuccess').replace('{format}', exportFormat.toUpperCase()));
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportPreview(null);
    setParsedData([]);

    // Parse preview
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target?.result as string;
        if (file.name.endsWith('.json')) {
          const data = JSON.parse(text);
          const items = Array.isArray(data) ? data : data.items || [];
          setParsedData(items);
          setImportPreview(items.slice(0, 5));
        } else {
          // CSV parsing
          const lines = text.split('\n').filter(l => l.trim());
          if (lines.length < 2) {
            showMsg('error', 'CSV file is empty or has no data rows');
            return;
          }
          const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
          const items = [];
          for (let i = 1; i < lines.length; i++) {
            const values = parseCSVLine(lines[i]);
            const item: any = {};
            headers.forEach((h, idx) => {
              if (values[idx] !== undefined && values[idx] !== '') {
                item[h] = values[idx];
              }
            });
            if (Object.keys(item).length > 0) items.push(item);
          }
          setParsedData(items);
          setImportPreview(items.slice(0, 5));
        }
      } catch (err) {
        showMsg('error', 'Failed to parse file. Please check the format.');
      }
    };
    reader.readAsText(file);
  };

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (line[i] === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += line[i];
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      showMsg('error', t('dataTransfer.noDataToImport'));
      return;
    }
    try {
      setImporting(true);
      const format = importFile?.name.endsWith('.json') ? 'json' : 'csv';
      const res = await dataTransferAPI.importData({
        items: parsedData,
        mode: importMode,
        format,
      });
      const count = res.data.imported || res.data.count || parsedData.length;
      showMsg('success', t('dataTransfer.importSuccess').replace('{count}', String(count)) + (importMode === 'overwrite' ? ' ' + t('dataTransfer.overwriteNote') : ''));
      setImportFile(null);
      setImportPreview(null);
      setParsedData([]);
      setShowConfirm(false);
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const fieldLabel = (field: any): string => {
    if (typeof field === 'object' && field !== null) {
      return field.label || field.key || 'Field';
    }
    const str = String(field);
    if (fieldLabels[str]) return fieldLabels[str];
    return str
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (s: string) => s.toUpperCase())
      .trim();
  };

  return (
    <div className="data-transfer">
      {message && (
        <div className={`dt-message ${message.type}`}>
          <FontAwesomeIcon icon={message.type === 'success' ? faCheckCircle : faExclamationTriangle} />
          {message.text}
        </div>
      )}

      <div className="dt-header">
        <h2><FontAwesomeIcon icon={faExchangeAlt} /> {t('dataTransfer.title')}</h2>
        <p>{t('dataTransfer.subtitle')}</p>
      </div>

      <div className="dt-tabs">
        <button className={`dt-tab ${activeSection === 'export' ? 'active' : ''}`} onClick={() => setActiveSection('export')}>
          <><FontAwesomeIcon icon={faFileExport} /> {t('dataTransfer.export')}</>
        </button>
        <button className={`dt-tab ${activeSection === 'import' ? 'active' : ''}`} onClick={() => setActiveSection('import')}>
          <><FontAwesomeIcon icon={faFileImport} /> {t('dataTransfer.import')}</>
        </button>
      </div>

      {/* Export Section */}
      {activeSection === 'export' && (
        <div className="dt-section">
          <div className="dt-card">
            <h3><FontAwesomeIcon icon={faDownload} /> {t('dataTransfer.exportTitle')}</h3>
            <p>{t('dataTransfer.exportDesc')}</p>

            <div className="dt-format-picker">
              <label>{t('dataTransfer.format')}</label>
              <div className="format-options">
                <button className={`format-btn ${exportFormat === 'csv' ? 'active' : ''}`} onClick={() => setExportFormat('csv')}>
                  <FontAwesomeIcon icon={faFileCsv} /> CSV
                </button>
                <button className={`format-btn ${exportFormat === 'json' ? 'active' : ''}`} onClick={() => setExportFormat('json')}>
                  <FontAwesomeIcon icon={faCode} /> JSON
                </button>
              </div>
            </div>

            <div className="dt-fields">
              <div className="dt-fields-header">
                <label>{t('dataTransfer.fieldsToInclude')}</label>
                <div className="dt-fields-actions">
                  <button className="btn-link" onClick={selectAllFields}>{t('dataTransfer.selectAll')}</button>
                  <button className="btn-link" onClick={deselectAllFields}>{t('dataTransfer.deselectAll')}</button>
                </div>
              </div>
              <div className="dt-fields-grid">
                {availableFields.map(field => (
                  <label key={field} className="dt-field-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedFields.includes(field)}
                      onChange={() => toggleField(field)}
                    />
                    <span>{fieldLabel(field)}</span>
                  </label>
                ))}
              </div>
            </div>

            <button className="btn-primary full-width" onClick={handleExport} disabled={exporting || selectedFields.length === 0}>
              {exporting ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> {t('dataTransfer.exporting')}</>
              ) : (
                <><FontAwesomeIcon icon={faDownload} /> {t('dataTransfer.download')} {exportFormat.toUpperCase()}</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Import Section */}
      {activeSection === 'import' && (
        <div className="dt-section">
          <div className="dt-card">
            <h3><FontAwesomeIcon icon={faUpload} /> {t('dataTransfer.importTitle')}</h3>
            <p>{t('dataTransfer.importDesc')}</p>

            <div className="dt-import-mode">
              <label>{t('dataTransfer.importMode')}</label>
              <div className="mode-options">
                <button
                  className={`mode-btn ${importMode === 'add' ? 'active' : ''}`}
                  onClick={() => setImportMode('add')}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  <div>
                    <strong>{t('dataTransfer.addToExisting')}</strong>
                    <small>{t('dataTransfer.addToExistingDesc')}</small>
                  </div>
                </button>
                <button
                  className={`mode-btn ${importMode === 'overwrite' ? 'active' : ''}`}
                  onClick={() => setImportMode('overwrite')}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  <div>
                    <strong>{t('dataTransfer.overwriteAll')}</strong>
                    <small>{t('dataTransfer.overwriteAllDesc')}</small>
                  </div>
                </button>
              </div>
            </div>

            {importMode === 'overwrite' && (
              <div className="dt-warning">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{t('dataTransfer.overwriteWarning')}</span>
              </div>
            )}

            <div className="dt-upload-area">
              <input
                type="file"
                id="import-file"
                accept=".csv,.json"
                onChange={handleFileSelect}
                hidden
              />
              <label htmlFor="import-file" className="upload-dropzone">
                {importFile ? (
                  <div className="file-selected">
                    <FontAwesomeIcon icon={importFile.name.endsWith('.json') ? faCode : faFileCsv} />
                    <span>{importFile.name}</span>
                    <small>{(importFile.size / 1024).toFixed(1)} KB</small>
                    <button className="btn-remove-file" onClick={(e) => { e.preventDefault(); setImportFile(null); setImportPreview(null); setParsedData([]); }}>
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <FontAwesomeIcon icon={faUpload} />
                    <span>{t('dataTransfer.clickToSelect')}</span>
                    <small>{t('dataTransfer.supported')}</small>
                  </div>
                )}
              </label>
            </div>

            {/* Preview */}
            {importPreview && importPreview.length > 0 && (
              <div className="dt-preview">
                <h4><FontAwesomeIcon icon={faInfoCircle} /> {t('dataTransfer.preview')} ({parsedData.length} {t('dataTransfer.itemsFound')})</h4>
                <div className="preview-table-wrapper">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        {Object.keys(importPreview[0]).slice(0, 5).map(key => (
                          <th key={key}>{fieldLabel(key)}</th>
                        ))}
                        {Object.keys(importPreview[0]).length > 5 && <th>...</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.map((row, i) => (
                        <tr key={i}>
                          {Object.values(row).slice(0, 5).map((val, j) => (
                            <td key={j}>{String(val || '').substring(0, 50)}</td>
                          ))}
                          {Object.keys(row).length > 5 && <td>...</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {parsedData.length > 5 && (
                  <p className="preview-note">{t('dataTransfer.showingOf').replace('{count}', String(parsedData.length))}</p>
                )}
              </div>
            )}

            <button
              className="btn-primary full-width"
              onClick={() => {
                if (importMode === 'overwrite') {
                  setShowConfirm(true);
                } else {
                  handleImport();
                }
              }}
              disabled={importing || parsedData.length === 0}
            >
              {importing ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> {t('dataTransfer.importing')}</>
              ) : (
                <><FontAwesomeIcon icon={faUpload} /> {t('dataTransfer.importItems').replace('{count}', String(parsedData.length))}</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Overwrite Confirmation */}
      {showConfirm && (
        <div className="modal-overlay" onClick={() => setShowConfirm(false)}>
          <div className="modal-content modal-danger" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FontAwesomeIcon icon={faExclamationTriangle} /> {t('dataTransfer.confirmOverwrite')}</h3>
              <button className="btn-close" onClick={() => setShowConfirm(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="modal-body">
              <p>{t('dataTransfer.overwriteBody1').replace('{count}', String(parsedData.length))}</p>
              <p>{t('dataTransfer.overwriteBody2')}</p>
              <div className="modal-actions">
                <button className="btn-cancel" onClick={() => setShowConfirm(false)}>{t('collections.cancel')}</button>
                <button className="btn-danger" onClick={handleImport} disabled={importing}>
                  {importing ? <><FontAwesomeIcon icon={faSpinner} spin /> {t('dataTransfer.importing')}</> : t('dataTransfer.yesOverwrite')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTransfer;
